'use client';

import { FC, FocusEvent, FocusEventHandler, useCallback, useMemo } from 'react';
import { FieldHelperProps, FieldInputProps, useField, ErrorMessage } from 'formik';

import Autocomplete, { AutocompleteProps } from '@/app/components/Autocomplete/Autocomplete';

interface FormAutocompleteProps
  extends Omit<
    AutocompleteProps,
    'onChange' | 'onBlur' | 'value' | 'isErrorVisible' | 'errorMessage' | 'isOptionsDropdownVisible'
  > {
  name: string;
  onChange?: (value: string | number) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>, field: FieldInputProps<any>, helpers: FieldHelperProps<any>) => void;
}

const FormAutocomplete: FC<FormAutocompleteProps> = ({ onChange, onBlur, ...props }) => {
  const [field, meta, helpers] = useField(props.name);

  const isErrorVisible = useMemo(() => {
    return !!(meta.error && meta.touched);
  }, [meta.error, meta.touched]);

  const handleChange = useCallback(
    (nextValue: string) => {
      helpers.setValue(nextValue);
      onChange?.(nextValue);
    },
    [helpers, onChange],
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      field.onBlur(event);
      onBlur?.(event, field, helpers);
    },
    [onBlur, field, helpers],
  );

  return (
    <Autocomplete
      {...props}
      onChange={handleChange}
      value={field.value}
      isErrorVisible={isErrorVisible}
      errorMessage={<ErrorMessage name={props.name} />}
      onBlur={handleBlur}
    />
  );
};

export default FormAutocomplete;
